import React from "react";
import { Wrench, Gauge, NotebookPen, Clock } from "lucide-react";
import FOHGuidancePanel from "./FOHGuidancePanel";

/**
 * Service Companion — skeleton for the Service path of the Yestimate Intake.
 * Section headers + sample field structure. Same atomic primitives as the
 * MOT Companion so Phase 2 wiring is consistent across all three companions.
 */
export default function ServiceCompanion({ data = {}, onChange = () => {} }) {
  const set = (key) => (e) => onChange({ [key]: e?.target ? e.target.value : e });

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4"
      data-testid="service-companion"
    >
      <div className="space-y-3">
        <CompanionHeader
          tone="teal"
          icon={<Wrench className="w-4 h-4" />}
          eyebrow="Reason · Service"
          title="Service intake"
          subtitle="Type of service + mileage + history — keep it light, the technician will go deep."
        />

        <Section
          icon={<Wrench className="w-3.5 h-3.5" />}
          label="Service type"
          testId="service-companion-type"
        >
          <FieldRow label="Which service?">
            <SegmentedGroup
              name="service_type"
              value={data.service_type}
              options={[
                "Interim / Oil & Filter",
                "Full Service",
                "Major (4-yr)",
                "Manufacturer Schedule",
                "Not sure",
              ]}
              onChange={(v) => onChange({ service_type: v })}
              testId="service-type"
            />
          </FieldRow>
          <Hint>
            If “Not sure” — we’ll advise based on mileage and the
            last interval before quoting.
          </Hint>
        </Section>

        <Section
          icon={<Gauge className="w-3.5 h-3.5" />}
          label="Mileage &amp; history"
          testId="service-companion-mileage"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldRow label="Current mileage">
              <input
                type="number"
                placeholder="e.g. 48,500"
                value={data.current_mileage || ""}
                onChange={set("current_mileage")}
                className="bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 focus:outline-none focus:border-teal-500"
                data-testid="service-current-mileage"
              />
            </FieldRow>
            <FieldRow label="Approx. last service date">
              <input
                type="text"
                placeholder="Month + year is fine"
                value={data.last_service_date || ""}
                onChange={set("last_service_date")}
                className="bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 focus:outline-none focus:border-teal-500"
                data-testid="service-last-date"
              />
            </FieldRow>
          </div>

          <FieldRow label="Annual mileage (rough)">
            <SegmentedGroup
              name="annual_mileage"
              value={data.annual_mileage}
              options={[
                "< 5,000",
                "5,000 – 10,000",
                "10,000 – 15,000",
                "15,000 – 20,000",
                "20,000+",
              ]}
              onChange={(v) => onChange({ annual_mileage: v })}
              testId="service-annual-mileage"
            />
          </FieldRow>
        </Section>

        <Section
          icon={<NotebookPen className="w-3.5 h-3.5" />}
          label="Concerns alongside the service"
          testId="service-companion-concerns"
        >
          <FieldRow label="Anything worth flagging?">
            <textarea
              rows={2}
              placeholder="Even small things — noises, warning lights, brake feel — get noted so the technician can look while it’s in."
              value={data.concerns_text || ""}
              onChange={set("concerns_text")}
              className="bg-white border border-slate-200 rounded-md px-3 py-2 text-[15px] text-slate-900 resize-none focus:outline-none focus:border-teal-500"
              data-testid="service-concerns-text"
            />
          </FieldRow>
        </Section>

        <Section
          icon={<Clock className="w-3.5 h-3.5" />}
          label="Collection preference"
          testId="service-companion-collection"
        >
          <FieldRow label="When would they like it back?">
            <SegmentedGroup
              name="collection_preference"
              value={data.collection_preference}
              options={[
                "Leaving with us for the day",
                "Waiting in",
                "End of day pickup",
                "Following day",
                "Flexible",
              ]}
              onChange={(v) => onChange({ collection_preference: v })}
              testId="service-collection"
            />
          </FieldRow>
        </Section>
      </div>

      <FOHGuidancePanel
        tone="teal"
        title="Service guidance"
        askNext={[
          "Does the customer know roughly when it was last serviced?",
          "Anything they've noticed since — even small noises or warning lights?",
        ]}
        sayToCustomer={[
          "A service is part of a broader inspection — we'll let you know if anything else is due by mileage or condition before doing extra work.",
          "We normally recommend leaving the vehicle for the day so we can inspect alongside the service properly.",
        ]}
        watchOuts={[
          "Waiting customers compress inspection time — set expectation early.",
        ]}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function CompanionHeader({ tone, icon, eyebrow, title, subtitle }) {
  const bg = TONE_HEADER[tone] || TONE_HEADER.teal;
  return (
    <div className={`relative overflow-hidden rounded-lg px-4 py-3 text-white ${bg}`}>
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,_white_0%,_transparent_55%)]" />
      <div className="relative flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-md bg-white/20 border border-white/30 flex items-center justify-center">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="text-[10.5px] uppercase tracking-[0.22em] font-semibold opacity-80">
            {eyebrow}
          </p>
          <h3 className="text-[17px] font-bold leading-tight truncate">{title}</h3>
          {subtitle && (
            <p className="text-[12.5px] opacity-90 leading-snug mt-0.5 max-w-[60ch]">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon, label, testId, children }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <header className="px-4 pt-3 pb-2 flex items-center gap-2 border-b border-slate-100">
        <span className="text-slate-500">{icon}</span>
        <p className="text-[11.5px] uppercase tracking-[0.18em] text-slate-700 font-semibold">
          {label}
        </p>
      </header>
      <div className="px-4 py-3.5 space-y-3">{children}</div>
    </section>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12.5px] font-semibold text-slate-700 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function Hint({ children }) {
  return <p className="text-[12px] text-slate-500 mt-1">{children}</p>;
}

function SegmentedGroup({ name, value, options, onChange, testId }) {
  return (
    <div className="flex gap-2 flex-wrap" data-testid={testId}>
      {options.map((opt) => {
        const isOn = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-full border text-[13.5px] font-medium transition-colors ${
              isOn
                ? "border-teal-500 bg-teal-50 text-teal-800"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            }`}
            data-testid={`${testId}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

const TONE_HEADER = {
  blue: "bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700",
  amber: "bg-gradient-to-r from-amber-600 via-orange-600 to-rose-600",
  teal: "bg-gradient-to-r from-teal-600 via-teal-700 to-emerald-700",
};
